import type { AnalysisType, VOI } from 'config/analysisConfig';
import { configStore } from 'config/configStore';
import { getConfiguredCprCrossSectionImageMode } from '@features/analysis/rendering/Panels/cprImageModeConfig';
import {
  filterImageLayerVisibilityForAnalysisType,
  getCprCrossSectionImagesFromParameter,
  resolveImageLayersWithRevision,
  selectNearestCprAngleFrame,
  type CprAngleFrame,
  type CprCrossSectionImages,
  type CprMode,
  type ImageLayerKey,
  type ResolvedImageLayerPaths,
} from '@features/analysis/rendering/Panels/cprImageResources';
import {
  isServerCprFrontDataBranchDeferred,
  isServerCprFrontDataImageManifest,
  isServerCprFrontDataAnalysisType,
  loadServerCprFrontDataBranchResourceOnce,
  loadServerCprFrontDataImageManifestOnce,
} from '@features/analysis/rendering/Panels/frontDataCprDebugLoader';
import { mergecprCrossSectionImagesBranchResource } from '@features/analysis/rendering/Panels/cprCrossSectionImages';
import {
  getServerMemoryImageData,
  isServerMemoryImageSource,
  resolveImageSourceQueued,
  retainServerImageCacheScope,
} from '@features/analysis/rendering/Panels/serverImageCache';
import { nearestCprPathArrayIndex } from '@features/analysis/rendering/Panels/cprPanel/cprImageOverlayGeometry';
import type { BufferPoolItem } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import type { ImageWindowingPolicy } from '@features/analysis/rendering/Panels/imageWindowing';

const REPORT_CPR_MODE: CprMode = 'straightened';
const REPORT_CPR_ANGLE = 0;
const REPORT_LAYER_ORDER: ImageLayerKey[] = [
  'base',
  'lumen',
  'lumenoutline',
  'plaque',
  'plaqueoutline',
  'fai',
  'aorta',
  'avc',
];

// Legacy report CPR captures render the straightened CPR into a narrow
// offscreen canvas around the centerline instead of using the full CPR image
// width. The front_data images are exported with a much wider lateral field of
// view, so server report captures must crop around the mapping centerline first
// to match the legacy popup layout.
const REPORT_RANGE_CPR_WIDTH_PX = 50;
const REPORT_LESION_CPR_WIDTH_PX = 70;

type ServerReportBaseArgs = {
  accessToken?: string | null;
  seriesId?: string | null;
  pool?: BufferPoolItem | null;
  analysisType: AnalysisType;
  currentVOI?: VOI;
  mainBranchIds?: readonly number[];
};

type ComposeReportCprArgs = ServerReportBaseArgs & {
  branchId: number | string;
  angleDeg?: number;
  mode?: CprMode;
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isServerReportCprEnabled(analysisType: AnalysisType) {
  return (
    getConfiguredCprCrossSectionImageMode() === 'server' &&
    isServerCprFrontDataAnalysisType(analysisType)
  );
}

function buildWindowingPolicy(currentVOI?: VOI): ImageWindowingPolicy | undefined {
  if (!currentVOI) return undefined;
  return {
    windowLevel: currentVOI.windowLevel,
    windowWidth: currentVOI.windowWidth,
    invert: currentVOI.invert,
  };
}

function buildLayerVisibility(analysisType: AnalysisType) {
  const cfg = configStore.get();
  const visibility = filterImageLayerVisibilityForAnalysisType(
    analysisType,
    cfg.label as Partial<Record<ImageLayerKey, boolean>>,
  );
  return {
    ...visibility,
    // Keep report captures consistent with current server CPR viewer defaults.
    lumenoutline: false,
    plaqueoutline: false,
  } as Partial<Record<ImageLayerKey, boolean>>;
}

function getManifestFromPool(pool?: BufferPoolItem | null) {
  return (
    getCprCrossSectionImagesFromParameter(pool?.cprCrossSectionImages?.data) ??
    getCprCrossSectionImagesFromParameter(pool?.parameter?.data)
  );
}

function hasReportFrameForBranch(
  manifest: CprCrossSectionImages | null | undefined,
  branchId: number | string,
) {
  const frames =
    manifest?.branches?.[String(branchId)]?.cpr?.[REPORT_CPR_MODE]
      ?.angleFrames;
  return Array.isArray(frames) && frames.length > 0;
}

async function ensureReportManifestBranchLoaded({
  accessToken,
  seriesId,
  manifest,
  branchId,
  analysisType,
}: {
  accessToken: string;
  seriesId: string;
  manifest: CprCrossSectionImages | null;
  branchId: number | string;
  analysisType: AnalysisType;
}) {
  if (!manifest) return null;
  if (isServerCprFrontDataBranchDeferred(manifest, branchId)) {
    const loaded = await loadServerCprFrontDataBranchResourceOnce({
      accessToken,
      seriesId,
      manifest,
      branchId,
      analysisType,
    });
    return mergecprCrossSectionImagesBranchResource(
      manifest,
      loaded.branchId,
      loaded.branch,
    );
  }
  return manifest;
}

async function getReportManifestWithBranch({
  accessToken,
  seriesId,
  pool,
  analysisType,
  currentVOI,
  mainBranchIds,
  branchId,
}: ServerReportBaseArgs & { branchId: number | string }) {
  if (!accessToken || !seriesId) return null;
  if (!isServerReportCprEnabled(analysisType)) return null;

  let manifest: CprCrossSectionImages | null = getManifestFromPool(pool);
  if (!manifest || !isServerCprFrontDataImageManifest(manifest)) {
    manifest = await loadServerCprFrontDataImageManifestOnce({
      accessToken,
      seriesId,
      analysisType,
      mainBranchIds,
      currentVOI,
    });
  }

  try {
    manifest = await ensureReportManifestBranchLoaded({
      accessToken,
      seriesId,
      manifest,
      branchId,
      analysisType,
    });
  } catch (err) {
    console.warn('[report][front_data] branch lazy load failed, retrying with fresh manifest', {
      analysisType,
      branchId,
      err,
    });
  }

  if (hasReportFrameForBranch(manifest, branchId)) return manifest;

  // Plaque report may need RCA/LAD/LCX captures even when the live CPR viewer
  // was initialized with only the current branch. If the user has rotated the
  // CPR view, the pool can contain a partial server manifest and the legacy
  // fallback produces blank report slots. Build a fresh main-branch manifest
  // for report capture and still render it with the original plaque overlay
  // policy below. Report CPR is fixed to 0 degrees.
  const reportManifestAnalysisType =
    analysisType === 'plaque' ? ('fai' as AnalysisType) : analysisType;

  try {
    let freshManifest = await loadServerCprFrontDataImageManifestOnce({
      accessToken,
      seriesId,
      analysisType: reportManifestAnalysisType,
      mainBranchIds,
      currentVOI,
    });
    freshManifest = await ensureReportManifestBranchLoaded({
      accessToken,
      seriesId,
      manifest: freshManifest,
      branchId,
      analysisType: reportManifestAnalysisType,
    });
    if (hasReportFrameForBranch(freshManifest, branchId)) {
      return freshManifest;
    }
  } catch (err) {
    console.warn('[report][front_data] fresh report manifest retry failed', {
      analysisType,
      branchId,
      err,
    });
  }

  return manifest;
}

function shouldDrawLayer(
  key: ImageLayerKey,
  layers: ResolvedImageLayerPaths,
  visibility: Partial<Record<ImageLayerKey, boolean>>,
) {
  if (!layers[key]) return false;
  if (key === 'base') return true;
  return visibility[key] !== false;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`image load failed: ${src.slice(0, 80)}`));
    image.src = src;
  });
}

function canvasToDataUrl(canvas: HTMLCanvasElement) {
  return canvas.toDataURL('image/png');
}

async function drawImageSource(
  ctx: CanvasRenderingContext2D,
  src: string,
  width: number,
  height: number,
) {
  if (isServerMemoryImageSource(src)) {
    const image = getServerMemoryImageData(src);
    if (!image) return;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = image.width;
    tempCanvas.height = image.height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;
    tempCtx.putImageData(new ImageData(image.rgba, image.width, image.height), 0, 0);
    ctx.drawImage(tempCanvas, 0, 0, width, height);
    return;
  }

  const image = await loadImage(src);
  ctx.drawImage(image, 0, 0, width, height);
}

function hexWithAlpha(hex: string | undefined, alpha: number) {
  const fallback = '#4dabf7';
  const h = (hex || fallback).replace('#', '').trim();
  const normalized = h.length === 3
    ? h.split('').map((c) => c + c).join('')
    : h.padEnd(6, '0').slice(0, 6);
  const r = parseInt(normalized.substring(0, 2), 16);
  const g = parseInt(normalized.substring(2, 4), 16);
  const b = parseInt(normalized.substring(4, 6), 16);
  return `rgba(${Number.isFinite(r) ? r : 77},${Number.isFinite(g) ? g : 171},${Number.isFinite(b) ? b : 247},${alpha})`;
}

function normalizeIndexRange(range: [number, number]): [number, number] {
  const start = Math.floor(Number(range[0]));
  const end = Math.floor(Number(range[1]));
  if (!Number.isFinite(start) || !Number.isFinite(end)) return [0, 0];
  return [Math.min(start, end), Math.max(start, end)];
}

function resolveFrameIndexY(frame: CprAngleFrame, index: number) {
  const path = frame.centerlinePath ?? [];
  if (!path.length) return 0;

  const nearestByCenterlineIndex = nearestCprPathArrayIndex(path, index);
  if (nearestByCenterlineIndex >= 0) {
    const point = path[nearestByCenterlineIndex];
    if (isFiniteNumber(point?.y)) return point.y;
  }

  const direct = path[Math.max(0, Math.min(path.length - 1, Math.floor(index)))];
  if (isFiniteNumber(direct?.y)) return direct.y;

  return 0;
}

function resolveFrameYRange(frame: CprAngleFrame, range: [number, number]) {
  const [startIndex, endIndex] = normalizeIndexRange(range);
  const y0 = resolveFrameIndexY(frame, startIndex);
  const y1 = resolveFrameIndexY(frame, endIndex);
  const startY = Math.max(0, Math.min(frame.height - 1, Math.min(y0, y1)));
  const endY = Math.max(0, Math.min(frame.height, Math.max(y0, y1)));
  return {
    startY,
    endY: Math.max(startY + 1, endY),
  };
}

function clampCropRect({
  imageWidth,
  imageHeight,
  x,
  y,
  width,
  height,
}: {
  imageWidth: number;
  imageHeight: number;
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  const safeImageWidth = Math.max(1, Math.floor(imageWidth));
  const safeImageHeight = Math.max(1, Math.floor(imageHeight));
  const safeWidth = Math.max(1, Math.floor(width));
  const safeHeight = Math.max(1, Math.floor(height));
  const sx = Math.max(0, Math.min(safeImageWidth - 1, Math.floor(x)));
  const sy = Math.max(0, Math.min(safeImageHeight - 1, Math.floor(y)));
  return {
    x: sx,
    y: sy,
    width: Math.max(1, Math.min(safeWidth, safeImageWidth - sx)),
    height: Math.max(1, Math.min(safeHeight, safeImageHeight - sy)),
  };
}

function cropDataUrlByRect({
  src,
  x,
  y,
  width,
  height,
}: {
  src: string;
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  return loadImage(src).then((image) => {
    const rect = clampCropRect({
      imageWidth: image.width,
      imageHeight: image.height,
      x,
      y,
      width,
      height,
    });
    const canvas = document.createElement('canvas');
    canvas.width = rect.width;
    canvas.height = rect.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return src;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
      image,
      rect.x,
      rect.y,
      rect.width,
      rect.height,
      0,
      0,
      rect.width,
      rect.height,
    );
    return canvasToDataUrl(canvas);
  });
}

function getFrameCenterX(frame: CprAngleFrame, indexRange?: [number, number]) {
  const candidates: number[] = [];
  const path = frame.centerlinePath ?? [];

  if (indexRange) {
    const [startIndex, endIndex] = normalizeIndexRange(indexRange);
    const midIndex = Math.round((startIndex + endIndex) / 2);
    const nearest = nearestCprPathArrayIndex(path, midIndex);
    if (nearest >= 0 && isFiniteNumber(path[nearest]?.x)) {
      candidates.push(path[nearest].x);
    }
  }

  const midPoint = path[Math.floor(path.length / 2)];
  if (isFiniteNumber(midPoint?.x)) candidates.push(midPoint.x);

  const finiteXs = path
    .map((point) => point.x)
    .filter((value): value is number => isFiniteNumber(value));
  if (finiteXs.length) {
    const sorted = [...finiteXs].sort((a, b) => a - b);
    candidates.push(sorted[Math.floor(sorted.length / 2)]);
  }

  candidates.push(frame.width * 0.5);
  return candidates.find(isFiniteNumber) ?? frame.width * 0.5;
}

function getReportLateralCropRect(
  frame: CprAngleFrame,
  widthPx: number,
  indexRange?: [number, number],
) {
  const cropWidth = Math.max(1, Math.min(frame.width, Math.round(widthPx)));
  const centerX = getFrameCenterX(frame, indexRange);
  const x = Math.max(0, Math.min(frame.width - cropWidth, Math.round(centerX - cropWidth / 2)));
  return {
    x,
    y: 0,
    width: cropWidth,
    height: frame.height,
  };
}

async function cropReportCprByRange({
  src,
  frame,
  indexRange,
  lateralWidthPx,
}: {
  src: string;
  frame: CprAngleFrame;
  indexRange?: [number, number];
  lateralWidthPx: number;
}) {
  const lateral = getReportLateralCropRect(frame, lateralWidthPx, indexRange);
  if (!indexRange) {
    return cropDataUrlByRect({
      src,
      x: lateral.x,
      y: 0,
      width: lateral.width,
      height: frame.height,
    });
  }

  const { startY, endY } = resolveFrameYRange(frame, indexRange);
  return cropDataUrlByRect({
    src,
    x: lateral.x,
    y: startY,
    width: lateral.width,
    height: Math.max(1, endY - startY),
  });
}

async function rotateDataUrl(src: string, degrees: number) {
  const image = await loadImage(src);
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const outW = Math.max(1, Math.ceil(image.width * cos + image.height * sin));
  const outH = Math.max(1, Math.ceil(image.width * sin + image.height * cos));
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return src;
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate(rad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  return canvasToDataUrl(canvas);
}

async function composeServerCprFrame({
  accessToken,
  seriesId,
  analysisType,
  currentVOI,
  branchId,
  angleDeg = REPORT_CPR_ANGLE,
  mode = REPORT_CPR_MODE,
  pool,
  mainBranchIds,
}: ComposeReportCprArgs) {
  const manifest = await getReportManifestWithBranch({
    accessToken,
    seriesId,
    pool,
    analysisType,
    currentVOI,
    mainBranchIds,
    branchId,
  });
  if (!manifest) return null;

  const frame = selectNearestCprAngleFrame(manifest, branchId, mode, angleDeg);
  if (!frame) return null;

  const layers = resolveImageLayersWithRevision(frame.layers, manifest.revision);
  if (!layers?.base) return null;

  const visibility = buildLayerVisibility(analysisType);
  const overlayPolicy = {
    analysisType,
    config: configStore.get() as Record<string, any>,
  };
  const windowingPolicy = buildWindowingPolicy(currentVOI);
  const resolvedLayers: Partial<Record<ImageLayerKey, string>> = {};
  const cacheScope = {
    seriesId: seriesId ?? null,
    revision: manifest.revision,
    branchId,
    view: 'cpr-report',
    mode,
    resourceMode: analysisType,
  };
  const releaseCacheScope = retainServerImageCacheScope(cacheScope);

  try {
    await Promise.all(
      REPORT_LAYER_ORDER.map(async (key) => {
        if (!shouldDrawLayer(key, layers, visibility)) return;
        const path = layers[key];
        if (!path) return;
        resolvedLayers[key] = await resolveImageSourceQueued(path, {
          seriesId: seriesId ?? undefined,
          accessToken: accessToken ?? undefined,
          revision: manifest.revision,
          width: frame.width,
          height: frame.height,
          priority: 50,
          visibility,
          overlayPolicy,
          windowingPolicy,
          cacheScope,
        });
      }),
    );

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, frame.width);
    canvas.height = Math.max(1, frame.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = false;

    for (const key of REPORT_LAYER_ORDER) {
      const src = resolvedLayers[key];
      if (!src) continue;
      await drawImageSource(ctx, src, frame.width, frame.height);
    }

    return {
      dataUrl: canvasToDataUrl(canvas),
      frame,
      manifest,
    };
  } finally {
    releaseCacheScope();
  }
}

export async function captureServerCprReportRange({
  accessToken,
  seriesId,
  pool,
  analysisType,
  currentVOI,
  mainBranchIds,
  branchId,
  indexRange,
  rotateDegrees = 0,
}: ServerReportBaseArgs & {
  branchId: number | string;
  indexRange: [number, number];
  rotateDegrees?: number;
}): Promise<[string, string] | null> {
  const composed = await composeServerCprFrame({
    accessToken,
    seriesId,
    pool,
    analysisType,
    currentVOI,
    mainBranchIds,
    branchId,
  });
  if (!composed) return null;

  const full = await cropReportCprByRange({
    src: composed.dataUrl,
    frame: composed.frame,
    lateralWidthPx: REPORT_RANGE_CPR_WIDTH_PX,
  });
  const crop = await cropReportCprByRange({
    src: composed.dataUrl,
    frame: composed.frame,
    indexRange,
    lateralWidthPx: REPORT_RANGE_CPR_WIDTH_PX,
  });

  if (!rotateDegrees) return [full, crop];
  return [
    await rotateDataUrl(full, rotateDegrees),
    await rotateDataUrl(crop, rotateDegrees),
  ];
}

export async function captureServerCprLesionImages({
  accessToken,
  seriesId,
  pool,
  analysisType,
  currentVOI,
  mainBranchIds,
  branchId,
  lesionRanges,
}: ServerReportBaseArgs & {
  branchId: number | string;
  lesionRanges: [number, number][];
}): Promise<[string[], string[]] | null> {
  const composed = await composeServerCprFrame({
    accessToken,
    seriesId,
    pool,
    analysisType,
    currentVOI,
    mainBranchIds,
    branchId,
  });
  if (!composed) return null;

  const cfg = configStore.get();
  const fullImages: string[] = [];
  const cropImages: string[] = [];
  const lateral = getReportLateralCropRect(
    composed.frame,
    REPORT_LESION_CPR_WIDTH_PX,
  );
  const reportFull = await cropDataUrlByRect({
    src: composed.dataUrl,
    x: lateral.x,
    y: 0,
    width: lateral.width,
    height: composed.frame.height,
  });

  for (let i = 0; i < lesionRanges.length; i += 1) {
    const range = lesionRanges[i];
    const { startY, endY } = resolveFrameYRange(composed.frame, range);

    const crop = await cropDataUrlByRect({
      src: composed.dataUrl,
      x: lateral.x,
      y: startY,
      width: lateral.width,
      height: Math.max(1, endY - startY),
    });
    cropImages.push(crop);

    const image = await loadImage(reportFull);
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      fullImages.push(reportFull);
      continue;
    }
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(image, 0, 0);
    ctx.save();
    ctx.fillStyle = hexWithAlpha(cfg.colors?.isrcontent?.[i], 0.2);
    ctx.fillRect(
      0,
      Math.floor(startY),
      image.width,
      Math.max(1, Math.ceil(endY - startY)),
    );
    ctx.restore();
    fullImages.push(canvasToDataUrl(canvas));
  }

  return [fullImages, cropImages];
}

export async function captureServerIsrReportImages({
  accessToken,
  seriesId,
  pool,
  analysisType,
  currentVOI,
  mainBranchIds,
  branchId,
  indexRange,
}: ServerReportBaseArgs & {
  branchId: number | string;
  indexRange: [number, number];
}): Promise<{ fullCpr: string; stent: string } | null> {
  const pair = await captureServerCprReportRange({
    accessToken,
    seriesId,
    pool,
    analysisType,
    currentVOI,
    mainBranchIds,
    branchId,
    indexRange,
    rotateDegrees: -90,
  });
  if (!pair) return null;
  return {
    fullCpr: pair[0],
    stent: pair[1],
  };
}


export async function captureServerAvcCprImages({
  accessToken,
  seriesId,
  pool,
  analysisType,
  currentVOI,
  mainBranchIds,
  branchId,
}: ServerReportBaseArgs & {
  branchId: number | string;
}): Promise<string[] | null> {
  if (!isServerReportCprEnabled(analysisType)) return null;

  const output: string[] = [];
  const angles = [0, 120, 240];
  for (const angleDeg of angles) {
    const composed =
      (await composeServerCprFrame({
        accessToken,
        seriesId,
        pool,
        analysisType,
        currentVOI,
        mainBranchIds,
        branchId,
        angleDeg,
        mode: 'avc',
      })) ??
      (await composeServerCprFrame({
        accessToken,
        seriesId,
        pool,
        analysisType,
        currentVOI,
        mainBranchIds,
        branchId,
        angleDeg,
        mode: REPORT_CPR_MODE,
      }));

    if (!composed) return null;
    output.push(composed.dataUrl);
  }

  return output;
}

export { isServerReportCprEnabled };
